// Typed client for the TESSA Lean auth + admin-users surface.
//
//   POST   /api/v1/auth/login              → AuthUser
//   POST   /api/v1/auth/logout             → void
//   GET    /api/v1/auth/me                 → AuthMe
//   POST   /api/v1/auth/change-password    → void
//   GET    /api/v1/admin/users             → { users: AdminUser[] }
//   POST   /api/v1/admin/users             → AdminUser
//   PATCH  /api/v1/admin/users/:id         → AdminUser
//   DELETE /api/v1/admin/users/:id         → void
//   POST   /api/v1/admin/reset-workspace   → { workspaceRoot, fixtureSource, wiped[] }
//
// Cookie-session-authenticated (httpOnly cookie set by /auth/login). Returns
// `Result<T, ApiError>` per the project's no-throws convention.

import type { Result } from '@tessa-lean/base';

import { apiCall, type ApiContext, type ApiError } from '../client.js';

export type AuthRole = 'admin' | 'user';

export interface AuthUser {
  id: string;
  username: string;
  role: AuthRole;
}

export interface AuthMe {
  id: string;
  username: string;
  role: AuthRole;
  lastLogin: string | null;
  tokenSpend: number;
  lastWorkspace: string | null;
}

export interface AdminUser {
  id: string;
  username: string;
  role: AuthRole;
  createdAt: string;
  lastLogin: string | null;
  tokenSpend: number;
  lastWorkspace: string | null;
  sessionCount: number;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface AdminCreateUserRequest {
  username: string;
  password: string;
  role: AuthRole;
}

export interface AdminUpdateUserRequest {
  password?: string;
  role?: AuthRole;
}

export async function loginApi(
  ctx: ApiContext,
  body: LoginRequest,
): Promise<Result<AuthUser, ApiError>> {
  return apiCall<LoginRequest, AuthUser>(ctx, 'POST', '/api/v1/auth/login', body);
}

export async function logoutApi(ctx: ApiContext): Promise<Result<undefined, ApiError>> {
  return apiCall<undefined, undefined>(ctx, 'POST', '/api/v1/auth/logout');
}

export async function meApi(ctx: ApiContext): Promise<Result<AuthMe, ApiError>> {
  return apiCall<undefined, AuthMe>(ctx, 'GET', '/api/v1/auth/me');
}

/**
 * Self-service password change for the currently signed-in user. Cookie-session
 * authenticated; the server re-verifies `currentPassword` before rotating.
 */
export async function changePasswordApi(
  ctx: ApiContext,
  body: ChangePasswordRequest,
): Promise<Result<undefined, ApiError>> {
  return apiCall<ChangePasswordRequest, undefined>(
    ctx,
    'POST',
    '/api/v1/auth/change-password',
    body,
  );
}

export async function listAdminUsersApi(
  ctx: ApiContext,
): Promise<Result<{ users: AdminUser[] }, ApiError>> {
  return apiCall<undefined, { users: AdminUser[] }>(ctx, 'GET', '/api/v1/admin/users');
}

export async function createAdminUserApi(
  ctx: ApiContext,
  body: AdminCreateUserRequest,
): Promise<Result<AdminUser, ApiError>> {
  return apiCall<AdminCreateUserRequest, AdminUser>(ctx, 'POST', '/api/v1/admin/users', body);
}

export async function updateAdminUserApi(
  ctx: ApiContext,
  id: string,
  body: AdminUpdateUserRequest,
): Promise<Result<AdminUser, ApiError>> {
  return apiCall<AdminUpdateUserRequest, AdminUser>(
    ctx,
    'PATCH',
    `/api/v1/admin/users/${encodeURIComponent(id)}`,
    body,
  );
}

export async function deleteAdminUserApi(
  ctx: ApiContext,
  id: string,
): Promise<Result<undefined, ApiError>> {
  return apiCall<undefined, undefined>(
    ctx,
    'DELETE',
    `/api/v1/admin/users/${encodeURIComponent(id)}`,
  );
}

export interface AdminResetWorkspaceRequest {
  /** Must equal `path.basename(workspaceRoot)` server-side. */
  confirmWorkspaceName: string;
}

export interface AdminResetWorkspaceResponse {
  workspaceRoot: string;
  fixtureSource: string;
  wiped: string[];
}

export async function resetWorkspaceApi(
  ctx: ApiContext,
  body: AdminResetWorkspaceRequest,
): Promise<Result<AdminResetWorkspaceResponse, ApiError>> {
  return apiCall<AdminResetWorkspaceRequest, AdminResetWorkspaceResponse>(
    ctx,
    'POST',
    '/api/v1/admin/reset-workspace',
    body,
  );
}
